import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../utils/api";
import { toParisTime, fmtAnswerTime, fmtDuration } from "../utils/date";
import Spinner from "../components/ui/Spinner";
import CollapsibleCard from "../components/ui/CollapsibleCard";

/* ── Composants génériques ────────────────────────────────────── */
function LazySection({ title, url, countFrom, render }) {
  const [open, setOpen]       = useState(false);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data === null && !loading) {
      setLoading(true);
      try { setData(await apiFetch(url)); }
      catch (e) { setError(e.message); }
      finally { setLoading(false); }
    }
  };

  const count = data && countFrom ? countFrom(data) : null;

  return (
    <div className="rounded-lg border border-gray-100 overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span className="text-sm font-medium text-gray-700 flex-1">{title}</span>
        {count != null && <span className="badge badge-neutral">{count}</span>}
        <span className="text-gray-400 text-xs">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-3 py-3 bg-white flex flex-col gap-2">
          {loading && <div className="flex items-center gap-2 text-sm text-gray-400"><Spinner /> Chargement…</div>}
          {error   && <div className="feedback-error">{error}</div>}
          {data    && render(data)}
        </div>
      )}
    </div>
  );
}

function SiteBlock({ siteId, label, count, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-gray-100 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50/80 hover:bg-gray-100 transition-colors text-left"
      >
        {siteId === "_MEE" && <span className="badge badge-violet shrink-0">MEE</span>}
        {siteId === "_NA"  && <span className="badge badge-neutral shrink-0">N/A</span>}
        <span className="text-sm font-medium text-gray-700 flex-1 truncate">{label}</span>
        {count != null && <span className="badge badge-neutral shrink-0">{count}</span>}
        <span className="text-gray-400 text-xs shrink-0">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="px-3 py-2.5 bg-white">{children}</div>}
    </div>
  );
}

function TypeFilterBar({ types, value, onChange }) {
  if (types.length <= 1) return null;
  return (
    <div className="flex gap-1.5 flex-wrap pb-2">
      <button
        onClick={() => onChange(null)}
        className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
          value === null ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
        }`}
      >
        Tous
      </button>
      {types.map(t => (
        <button
          key={t}
          onClick={() => onChange(value === t ? null : t)}
          className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
            value === t ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Section Kizeo
   ══════════════════════════════════════════════════════════════════ */
function KizeoSection({ siteName }) {
  return (
    <CollapsibleCard icon="📋" title="Kizeo">
      <div className="px-4 pb-4 pt-3 flex flex-col gap-3">
        <LazySection
          title="Modèles Kizeo"
          url="/api/data/forms/models-stats"
          countFrom={d => d.models_stats?.length}
          render={data => (
            <table className="w-full text-xs text-gray-700">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left pb-1 font-medium">ID</th>
                  <th className="text-left pb-1 font-medium">Nom</th>
                  <th className="text-right pb-1 font-medium">Formulaires</th>
                </tr>
              </thead>
              <tbody>
                {(data.models_stats || []).map(m => (
                  <tr key={m.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-1 font-mono text-gray-500">{m.id}</td>
                    <td className="py-1">{m.name}</td>
                    <td className="py-1 text-right font-medium">{m.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        />

        <LazySection
          title="Formulaires par site"
          url="/api/data/forms/by-site-list"
          countFrom={d => d.by_site?.length}
          render={data => (
            <div className="flex flex-col gap-1.5">
              {(data.by_site || []).map(site => {
                const sorted = [...site.forms].sort((a, b) => {
                  if (!a.answer_time && !b.answer_time) return 0;
                  if (!a.answer_time) return 1;
                  if (!b.answer_time) return -1;
                  return new Date(b.answer_time.replace(" ", "T")) - new Date(a.answer_time.replace(" ", "T"));
                });
                return (
                  <SiteBlock key={site.site_id} siteId={site.site_id} label={siteName(site.site_id)} count={sorted.length}>
                    <ul className="flex flex-col gap-0.5">
                      {sorted.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs py-0.5 border-b border-gray-50 last:border-0">
                          <span className="font-mono text-gray-400 truncate flex-1">{f.file}</span>
                          <span className="text-gray-500 shrink-0">{fmtAnswerTime(f.answer_time)}</span>
                        </li>
                      ))}
                    </ul>
                  </SiteBlock>
                );
              })}
            </div>
          )}
        />
      </div>
    </CollapsibleCard>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Section Syrinix
   ══════════════════════════════════════════════════════════════════ */
function SyrinixSection({ siteName }) {
  const renderEventList = (sites) => (
    <div className="flex flex-col gap-1.5">
      {sites.map(site => (
        <SiteBlock key={site.site_id} siteId={site.site_id} label={siteName(site.site_id)} count={site.events?.length}>
          <ul className="flex flex-col gap-0.5">
            {(site.events || []).map((e, i) => {
              const dur = fmtDuration(e.startTime, e.endTime);
              return (
                <li key={i} className="flex items-baseline gap-2 text-xs py-0.5 border-b border-gray-50 last:border-0">
                  <span className="font-mono text-gray-400 shrink-0 w-16">#{e.ID}</span>
                  <span className="text-gray-600 flex-1">
                    {toParisTime(e.startTime)} → {toParisTime(e.endTime)}
                  </span>
                  {dur && <span className="text-gray-400 shrink-0">{dur}</span>}
                  {e.volume_l != null && <span className="text-violet-500 shrink-0">{e.volume_l} L</span>}
                </li>
              );
            })}
          </ul>
        </SiteBlock>
      ))}
    </div>
  );

  return (
    <CollapsibleCard icon="⚡" title="Syrinix">
      <div className="px-4 pb-4 pt-3 flex flex-col gap-3">
        <LazySection
          title="Événements bruts"
          url="/api/data/syrinix/raw-list"
          countFrom={d => d["events-raw"]?.length}
          render={data => renderEventList(data["events-raw"] || [])}
        />
        <LazySection
          title="Événements fusionnés"
          url="/api/data/syrinix/merged-auto-list"
          countFrom={d => d["events-merged-auto"]?.length}
          render={data => renderEventList(data["events-merged-auto"] || [])}
        />
        <LazySection
          title="Essais détectés (algorithme final)"
          url="/api/essais/detected-list"
          countFrom={d => d["essais-detected"]?.length}
          render={data => renderEventList(data["essais-detected"] || [])}
        />
      </div>
    </CollapsibleCard>
  );
}

/* EventVolume ("Voir volume événement") supprimé le 21/07/2026 (décision Jordan) —
   servait uniquement à calibrer l'ancienne approche "rang journalier" (comptage par
   volume), abandonnée. Voir docs/REFONTE_ASSOCIATION.md §24. */

/* ══════════════════════════════════════════════════════════════════
   Sous-section : Formulaires → Événements (avec filtre type)
   ══════════════════════════════════════════════════════════════════ */
function AssocFormsSection({ siteName }) {
  const [open, setOpen]             = useState(false);
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data === null && !loading) {
      setLoading(true);
      try { setData(await apiFetch("/api/match/associated-table")); }
      catch (e) { setError(e.message); }
      finally { setLoading(false); }
    }
  };

  const allTypes = useMemo(() => {
    if (!data) return [];
    const s = new Set();
    Object.values(data).forEach(events =>
      Object.values(events).forEach(ev =>
        (ev.forms || []).forEach(f => { if (f.type_de_controle) s.add(f.type_de_controle); })
      )
    );
    return [...s].sort();
  }, [data]);

  const totalCount = useMemo(() => {
    if (!data) return null;
    return Object.values(data).reduce((sum, events) =>
      sum + Object.values(events).reduce((s, ev) => s + (ev.forms || []).length, 0), 0);
  }, [data]);

  return (
    <div className="rounded-lg border border-gray-100 overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span className="text-sm font-medium text-gray-700 flex-1">Formulaires → Événements</span>
        {totalCount != null && <span className="badge badge-neutral">{totalCount}</span>}
        <span className="text-gray-400 text-xs">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="px-3 py-3 bg-white flex flex-col gap-2">
          {loading && <div className="flex items-center gap-2 text-sm text-gray-400"><Spinner /> Chargement…</div>}
          {error   && <div className="feedback-error">{error}</div>}
          {data && (
            <>
              <TypeFilterBar types={allTypes} value={typeFilter} onChange={setTypeFilter} />
              <div className="flex flex-col gap-1.5">
                {Object.entries(data).map(([siteId, events]) => {
                  const filteredEvents = Object.entries(events).map(([eid, ev]) => ({
                    eid,
                    ev,
                    forms: (ev.forms || []).filter(f => !typeFilter || f.type_de_controle === typeFilter),
                  })).filter(x => x.forms.length > 0);
                  if (filteredEvents.length === 0) return null;
                  const total = filteredEvents.reduce((s, x) => s + x.forms.length, 0);
                  return (
                    <SiteBlock key={siteId} siteId={siteId} label={siteName(siteId)} count={total}>
                      <div className="flex flex-col gap-2">
                        {filteredEvents.map(({ eid, ev, forms }) => (
                          <div key={eid} className="text-xs">
                            <div className="flex items-center gap-2 font-medium text-gray-600 mb-1">
                              <span className="font-mono">#{eid}</span>
                              {ev.startTime && (
                                <span className="text-gray-400 font-normal">
                                  {toParisTime(ev.startTime)} → {toParisTime(ev.endTime)}
                                  {fmtDuration(ev.startTime, ev.endTime) && (
                                    <span className="ml-1 text-gray-300">({fmtDuration(ev.startTime, ev.endTime)})</span>
                                  )}
                                </span>
                              )}
                              <span className="badge badge-neutral ml-auto">{forms.length} form.</span>
                            </div>
                            <ul className="pl-3 flex flex-col gap-0.5">
                              {forms.map((f, i) => (
                                <li key={i} className="flex items-center gap-2 text-gray-500 border-b border-gray-50 last:border-0 py-0.5">
                                  <span className="font-mono">#{f.form_id}</span>
                                  {f.type_de_controle && (
                                    <span className="badge badge-neutral text-gray-400">{f.type_de_controle}</span>
                                  )}
                                  {f.manual && (
                                    <span className="badge badge-info text-blue-600 text-[9px]">M</span>
                                  )}
                                  <span className="text-gray-400 ml-auto">{fmtAnswerTime(f.answer_time)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </SiteBlock>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Sous-section : No-match
   ══════════════════════════════════════════════════════════════════ */
function NoMatchSection({ siteName }) {
  const [open, setOpen]             = useState(false);
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data === null && !loading) {
      setLoading(true);
      try { setData(await apiFetch("/api/match/no-match-table")); }
      catch (e) { setError(e.message); }
      finally { setLoading(false); }
    }
  };

  const allTypes = useMemo(() => {
    if (!data) return [];
    const s = new Set();
    Object.values(data).forEach(forms =>
      forms.forEach(f => { if (f.type_de_controle) s.add(f.type_de_controle); })
    );
    return [...s].sort();
  }, [data]);

  const totalCount = data
    ? Object.values(data).reduce((s, forms) => s + forms.length, 0)
    : null;

  return (
    <div className="rounded-lg border border-gray-100 overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span className="text-sm font-medium text-gray-700 flex-1">Sans correspondance Syrinix</span>
        {totalCount != null && (
          <span className={`badge ${totalCount > 0 ? "badge-warning" : "badge-neutral"}`}>{totalCount}</span>
        )}
        <span className="text-gray-400 text-xs">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="px-3 py-3 bg-white flex flex-col gap-2">
          {loading && <div className="flex items-center gap-2 text-sm text-gray-400"><Spinner /> Chargement…</div>}
          {error   && <div className="feedback-error">{error}</div>}
          {data && totalCount === 0 && (
            <p className="text-sm text-gray-400 py-1">Tous les formulaires ont une correspondance Syrinix.</p>
          )}
          {data && totalCount > 0 && (
            <>
              <TypeFilterBar types={allTypes} value={typeFilter} onChange={setTypeFilter} />
              <div className="flex flex-col gap-1.5">
                {Object.entries(data).map(([siteId, forms]) => {
                  const filtered = forms.filter(f => !typeFilter || f.type_de_controle === typeFilter);
                  if (filtered.length === 0) return null;
                  return (
                    <SiteBlock key={siteId} siteId={siteId} label={siteName(siteId)} count={filtered.length}>
                      <ul className="flex flex-col gap-0.5">
                        {filtered.map((f, i) => (
                          <li key={i} className="flex items-center gap-2 text-xs py-0.5 border-b border-gray-50 last:border-0">
                            <span className="font-mono text-gray-400">#{f.form_id}</span>
                            {f.type_de_controle && (
                              <span className="badge badge-neutral">{f.type_de_controle}</span>
                            )}
                            <span className="text-gray-400 ml-auto">{fmtAnswerTime(f.answer_time)}</span>
                          </li>
                        ))}
                      </ul>
                    </SiteBlock>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Section Associations
   ══════════════════════════════════════════════════════════════════ */
function AssociationsSection({ siteName }) {
  return (
    <CollapsibleCard icon="🔗" title="Associations">
      <div className="px-4 pb-4 pt-3 flex flex-col gap-3">
        <AssocFormsSection siteName={siteName} />

        <NoMatchSection siteName={siteName} />

        <LazySection
          title="Mises en Eau"
          url="/api/match/mee-table"
          countFrom={d => Object.values(d).reduce((s, forms) => s + forms.length, 0)}
          render={data => {
            const entries = Object.entries(data);
            if (entries.length === 0) return <p className="text-sm text-gray-400 py-2">Aucune mise en eau trouvée.</p>;
            return (
              <div className="flex flex-col gap-1.5">
                {entries.map(([siteId, forms]) => (
                  <SiteBlock key={siteId} siteId={siteId} label={siteName(siteId)} count={forms.length}>
                    <ul className="flex flex-col gap-0.5">
                      {forms.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs py-0.5 border-b border-gray-50 last:border-0">
                          <span className="font-mono text-gray-400">#{f.form_id}</span>
                          {f.code_sdis66 && <span className="badge badge-violet shrink-0">{f.code_sdis66}</span>}
                          <span className="text-gray-500 ml-auto">{fmtAnswerTime(f.answer_time)}</span>
                        </li>
                      ))}
                    </ul>
                  </SiteBlock>
                ))}
              </div>
            );
          }}
        />
      </div>
    </CollapsibleCard>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Page
   ══════════════════════════════════════════════════════════════════ */
export default function Visionnement() {
  const [siteNames, setSiteNames] = useState(new Map());

  useEffect(() => {
    apiFetch("/api/assoc/sites")
      .then(data => {
        const m = new Map();
        (Array.isArray(data) ? data : []).forEach(s => {
          if (s.site_id) m.set(s.site_id, s.label);
        });
        setSiteNames(m);
      })
      .catch(() => {});
  }, []);

  const siteName = (id) => {
    if (id === "_NA")  return "Non assigné";
    if (id === "_MEE") return "Mises en Eau";
    return siteNames.get(id) || id;
  };

  return (
    <div className="flex flex-col gap-3">
      <KizeoSection  siteName={siteName} />
      <SyrinixSection siteName={siteName} />
      <AssociationsSection siteName={siteName} />
    </div>
  );
}
